/**
 * A plain textarea that pops a suggestion list when you type "@".
 *
 *   - Storage stays text-only — what the user types IS what gets saved.
 *   - On pick, the popover inserts the token at the caret (e.g. "@Alice
 *     " or "@friday "). The user can also just type tokens by hand;
 *     the server parses them either way.
 *   - No contentEditable, no DOM mutation tricks. Old-school.
 *
 * Tabs:
 *   - People: filtered board-member list
 *   - Dates:  Today / Tomorrow / weekdays / pick a date (native input)
 *
 * Keyboard:
 *   - ↑ ↓     navigate
 *   - Tab     switch tabs
 *   - Enter   pick (or, if popover closed, insert a newline)
 *   - Cmd/Ctrl+Enter   submit
 *   - Esc     close popover
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar } from './Avatar';

export interface MemberRef {
  id: string;
  name: string;
  avatarHue: number;
  avatarUrl: string | null;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSubmit?: () => void;
  members: MemberRef[];
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
}

interface Popover {
  tab: 'people' | 'dates' | 'times';
  query: string;
  /** Caret index of the '@' that opened the popover. */
  atIndex: number;
  activeIdx: number;
}

const QUICK_DATES = [
  { label: 'Today', insert: 'today' },
  { label: 'Tomorrow', insert: 'tomorrow' },
  { label: 'Monday', insert: 'monday' },
  { label: 'Tuesday', insert: 'tuesday' },
  { label: 'Wednesday', insert: 'wednesday' },
  { label: 'Thursday', insert: 'thursday' },
  { label: 'Friday', insert: 'friday' },
  { label: 'Saturday', insert: 'saturday' },
  { label: 'Sunday', insert: 'sunday' },
];

const QUICK_TIMES = [
  { label: '9:00 am', insert: '9am' },
  { label: '10:00 am', insert: '10am' },
  { label: 'Noon', insert: 'noon' },
  { label: '1:00 pm', insert: '1pm' },
  { label: '2:00 pm', insert: '2pm' },
  { label: '3:00 pm', insert: '3pm' },
  { label: '5:00 pm', insert: '5pm' },
];

export function MentionTextarea({
  value,
  onChange,
  onSubmit,
  members,
  placeholder = 'Add a comment… (@ to tag someone or pick a date)',
  disabled,
  rows = 2,
}: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [popover, setPopover] = useState<Popover | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ left: number; top: number } | null>(null);
  const [pickerDate, setPickerDate] = useState('');
  const [pickerTime, setPickerTime] = useState('');
  const dateInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  // 'pointer: coarse' is the platform-standard proxy for touch-first
  // devices (phones / tablets). When we detect it AND the user opens
  // the Dates or Times tab, we immediately invoke the native picker
  // via showPicker() so they get the iOS/Android scroll wheel rather
  // than the desktop quick-list.
  const isTouch =
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(pointer: coarse)').matches
      : false;

  const items: PickItem[] = popover ? buildItems(popover, members) : [];

  // Recompute popover.query when value changes after the @.
  useEffect(() => {
    if (!popover) return;
    // If the @ at popover.atIndex was deleted, close.
    if (value[popover.atIndex] !== '@') {
      setPopover(null);
      return;
    }
    const after = value.slice(popover.atIndex + 1);
    // The "query" is the run of non-whitespace characters right after the @.
    const m = /^[^\s]*/.exec(after);
    const q = m?.[0] ?? '';
    if (q !== popover.query) setPopover({ ...popover, query: q, activeIdx: 0 });
  }, [value, popover]);

  // Position the popover. We could measure the caret precisely, but for
  // a textarea that's a research project — just anchor to the textarea's
  // bottom-left, with a small offset. Good enough for a hackathon.
  // When opening the Dates / Times tab on a touch device, jump
  // straight to the OS native picker. Desktop users still see the
  // popover with quick options.
  useEffect(() => {
    if (!popover || !isTouch) return;
    if (popover.tab === 'dates' && dateInputRef.current) {
      const el = dateInputRef.current;
      // showPicker is the modern API (Chrome 99+, Safari 16+). Falls
      // back to a focus-and-click which fires the native picker on
      // Mobile Safari.
      queueMicrotask(() => {
        try {
          (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
        } catch {
          el.focus();
          el.click();
        }
      });
    } else if (popover.tab === 'times' && timeInputRef.current) {
      const el = timeInputRef.current;
      queueMicrotask(() => {
        try {
          (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.();
        } catch {
          el.focus();
          el.click();
        }
      });
    }
  }, [popover, isTouch]);

  useEffect(() => {
    if (!popover || !taRef.current) {
      setPopoverPos(null);
      return;
    }
    const update = () => {
      const r = taRef.current!.getBoundingClientRect();
      setPopoverPos({ left: r.left, top: r.bottom + 4 });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [popover]);

  const insertToken = useCallback(
    (token: string) => {
      if (!popover) return;
      const before = value.slice(0, popover.atIndex);
      const afterStart = popover.atIndex + 1 + popover.query.length;
      const after = value.slice(afterStart);
      // Ensure a trailing space so the next char doesn't fuse with the token.
      const spacer = after.startsWith(' ') ? '' : ' ';
      const next = `${before}@${token}${spacer}${after}`;
      onChange(next);
      setPopover(null);
      // Restore caret after the inserted token.
      const newCaret = before.length + 1 + token.length + spacer.length;
      requestAnimationFrame(() => {
        const ta = taRef.current;
        if (!ta) return;
        ta.focus();
        ta.setSelectionRange(newCaret, newCaret);
      });
    },
    [popover, value, onChange],
  );

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (disabled) return;
    if (popover) {
      const list = items;
      if (e.key === 'Escape') {
        setPopover(null);
        e.preventDefault();
        return;
      }
      if (e.key === 'Tab') {
        const order: Popover['tab'][] = ['people', 'dates', 'times'];
        const nextTab = order[(order.indexOf(popover.tab) + 1) % order.length]!;
        setPopover({ ...popover, tab: nextTab, activeIdx: 0 });
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowDown' && list.length > 0) {
        setPopover({ ...popover, activeIdx: (popover.activeIdx + 1) % list.length });
        e.preventDefault();
        return;
      }
      if (e.key === 'ArrowUp' && list.length > 0) {
        setPopover({
          ...popover,
          activeIdx: (popover.activeIdx - 1 + list.length) % list.length,
        });
        e.preventDefault();
        return;
      }
      if (e.key === 'Enter') {
        const pick = list[popover.activeIdx];
        if (pick) {
          insertToken(pick.insert);
          e.preventDefault();
          return;
        }
      }
    }

    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onSubmit?.();
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const v = e.target.value;
    onChange(v);

    // Detect a fresh "@" typed at the caret.
    const caret = e.target.selectionStart ?? v.length;
    if (caret > 0 && v[caret - 1] === '@') {
      const prev = caret >= 2 ? v[caret - 2] ?? '' : '';
      // Only open at start-of-line or after whitespace/punctuation.
      if (prev === '' || /[\s(]/.test(prev)) {
        setPopover({ tab: 'people', query: '', atIndex: caret - 1, activeIdx: 0 });
      }
    }
  }

  function handleBlur() {
    // Delay so a popover click registers before we collapse.
    setTimeout(() => setPopover((p) => (document.activeElement === taRef.current ? p : null)), 100);
  }

  return (
    <>
      <textarea
        ref={taRef}
        className="input min-h-[44px] flex-1 resize-none text-sm"
        rows={rows}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />

      {popover && popoverPos && (
        <div
          className="mention-popover"
          style={{ position: 'fixed', left: popoverPos.left, top: popoverPos.top, zIndex: 80 }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex border-b border-ink-rim">
            <button
              type="button"
              className="mention-popover-tab"
              data-active={popover.tab === 'people'}
              onClick={() => setPopover({ ...popover, tab: 'people', activeIdx: 0 })}
            >
              People
            </button>
            <button
              type="button"
              className="mention-popover-tab"
              data-active={popover.tab === 'dates'}
              onClick={() => setPopover({ ...popover, tab: 'dates', activeIdx: 0 })}
            >
              Dates
            </button>
            <button
              type="button"
              className="mention-popover-tab"
              data-active={popover.tab === 'times'}
              onClick={() => setPopover({ ...popover, tab: 'times', activeIdx: 0 })}
            >
              Times
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 && popover.tab === 'people' && (
              <div className="px-3 py-3 text-xs text-cream-3">No people match.</div>
            )}
            {items.map((it, i) => (
              <button
                key={`${it.kind}-${it.insert}`}
                type="button"
                className="mention-popover-row"
                data-active={i === popover.activeIdx}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertToken(it.insert);
                }}
                onMouseEnter={() => setPopover({ ...popover, activeIdx: i })}
              >
                {it.kind === 'person' && (
                  <Avatar
                    user={{ id: it.userId, name: it.label, avatarHue: it.avatarHue, avatarUrl: it.avatarUrl }}
                    size={20}
                  />
                )}
                {it.kind === 'date' && (
                  <span className="text-xs" style={{ width: 20, textAlign: 'center' }}>📅</span>
                )}
                <span>{it.label}</span>
              </button>
            ))}
            {popover.tab === 'dates' && (
              <div className="flex items-center gap-2 border-t border-ink-rim/60 px-3 py-2 text-xs text-cream-3">
                <span>Pick:</span>
                <input
                  ref={dateInputRef}
                  type="date"
                  className="input flex-1 !py-1 text-xs"
                  value={pickerDate}
                  onChange={(e) => {
                    setPickerDate(e.target.value);
                    // On touch devices we auto-commit the moment the
                    // user picks a date — they already tapped 'done'
                    // on the native picker, no need for a second tap.
                    if (isTouch && e.target.value) {
                      insertToken(e.target.value);
                      setPickerDate('');
                    }
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  className="btn btn-ghost !px-2 !py-1 text-xs"
                  disabled={!pickerDate}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (pickerDate) insertToken(pickerDate);
                    setPickerDate('');
                  }}
                >
                  Use
                </button>
              </div>
            )}
            {popover.tab === 'times' && (
              <div className="flex items-center gap-2 border-t border-ink-rim/60 px-3 py-2 text-xs text-cream-3">
                <span>Pick:</span>
                <input
                  ref={timeInputRef}
                  type="time"
                  className="input flex-1 !py-1 text-xs"
                  value={pickerTime}
                  onChange={(e) => {
                    setPickerTime(e.target.value);
                    if (isTouch && e.target.value) {
                      insertToken(e.target.value);
                      setPickerTime('');
                    }
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  className="btn btn-ghost !px-2 !py-1 text-xs"
                  disabled={!pickerTime}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (pickerTime) insertToken(pickerTime);
                    setPickerTime('');
                  }}
                >
                  Use
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

type PickItem =
  | { kind: 'person'; insert: string; userId: string; label: string; avatarHue: number; avatarUrl: string | null }
  | { kind: 'date'; insert: string; label: string };

function buildItems(p: Popover, members: MemberRef[]): PickItem[] {
  const q = p.query.toLowerCase();
  if (p.tab === 'people') {
    return members
      .filter((m) => !q || m.name.toLowerCase().includes(q))
      .slice(0, 6)
      .map((m) => ({
        kind: 'person',
        insert: m.name,
        userId: m.id,
        label: m.name,
        avatarHue: m.avatarHue,
        avatarUrl: m.avatarUrl,
      }));
  }
  if (p.tab === 'dates') {
    return QUICK_DATES.filter((d) => !q || d.label.toLowerCase().startsWith(q)).map((d) => ({
      kind: 'date',
      insert: d.insert,
      label: d.label,
    }));
  }
  return QUICK_TIMES.filter((t) => !q || t.label.toLowerCase().startsWith(q)).map((t) => ({
    kind: 'date',
    insert: t.insert,
    label: t.label,
  }));
}
